// Escalas dos gráficos da recomendação. Puro, sem React: é a parte que erra em
// silêncio (um eixo mal escolhido não quebra a tela, só conta a história errada), então mora
// separada e testada.

/**
 * Domínio simétrico em torno do zero — para desvio vs base, onde o zero é a referência.
 *
 * A folga é generosa (45%) porque o rótulo do valor anda colado à ponta da barra: sem ela, a
 * barra mais longa encostaria no número que a descreve.
 */
export function deviationDomain(values: number[], padPct = 0.45): number {
  const max = Math.max(...values.map((v) => Math.abs(v)), 0);
  // Tudo igual à base ⇒ largura 1 evita divisão por zero (todas as barras somem, que é o correto).
  return max === 0 ? 1 : max * (1 + padPct);
}

/**
 * Largura da barra de desvio, em % da METADE do trilho em que ela cresce.
 *
 * O piso de 2% mantém visível um Δ minúsculo (19 s de ganho ainda são um ganho), mas desvio
 * EXATAMENTE zero não desenha nada: empatar com a base é um resultado, e uma barra de cortesia
 * ali diria que houve diferença.
 */
export function deviationWidthPct(value: number, domain: number): number {
  if (!Number.isFinite(value) || domain <= 0 || value === 0) return 0;
  return Math.min(100, Math.max(2, (Math.abs(value) / domain) * 100));
}

/**
 * Faixa RECORTADA em torno dos valores observados.
 *
 * OEE de 93,8 e 94,4 num eixo de 0 a 100 são a mesma barra: a diferença que decide a recomendação
 * fica invisível. O recorte é honesto desde que a faixa esteja rotulada nas pontas — quem desenha
 * é responsável por mostrá-la (ver `IndicatorCompare`). `minSpan` impede que valores idênticos
 * gerem uma faixa de largura zero, onde qualquer ruído viraria uma diferença enorme.
 */
export function zoomedDomain(values: number[], minSpan = 0.2, padRatio = 0.55): [number, number] {
  const usable = values.filter((v) => Number.isFinite(v));
  if (usable.length === 0) return [0, 1];
  const lo = Math.min(...usable);
  const hi = Math.max(...usable);
  const pad = Math.max(hi - lo, minSpan) * padRatio;
  return [lo - pad, hi + pad];
}

/** Posição de um valor no domínio, em % (0–100). Fora do domínio, grampeia. */
export function positionPct(value: number, [min, max]: [number, number]): number {
  if (!Number.isFinite(value) || max <= min) return 50;
  return Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
}

/** Fração de um valor sobre o máximo da série, em % — eixo ancorado no zero. */
export function magnitudePct(value: number, max: number): number {
  if (!Number.isFinite(value) || max <= 0) return 0;
  return Math.min(100, Math.max(0, (value / max) * 100));
}
